import { Blac, Cubit } from "blac-next";
import AppViewCubit from "src/state/AppViewCubit/AppViewCubit";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import translate from "src/lib/translate";
import UserPreferencesCubit, {
  UserPreferenceKeys
} from "src/state/UserPreferencesCubit/UserPreferencesCubit";
import AnnouncementPopupCubit from "src/ui/components/AnnouncementPopup/AnnouncementPopupCubit";
import { TourGuideCubit } from "@9amhealth/shared";
import createChatTour, { CHAT_TOUR_ID, type ChatTourVariant } from "./chatTour";
import { ConversationService } from "./ConversationService";
import hiImSophieImage from "../hi_im_sophie.webp";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { TrackEvent, TrackType } from "src/state/Track/TrackCubit";
import { tracker } from "src/state/state";
import { APP_CHAT_PATH } from "src/lib/createAppPath";
import { chatConversationPath } from "../leapChatPaths";
import { ConversationResponse } from "@9amhealth/openapi";

export interface LeapChatRouterState {
  lastChatPath: string | null;
  autoSelectPath: string | null;
  checking: boolean;
  showAnnouncement: boolean;
  conversationHistoryId: string | undefined;
  hasResolvedSession: boolean;
  shouldOpenDrawer: boolean;
}

export default class LeapChatRouterCubit extends Cubit<LeapChatRouterState> {
  static keepAlive = true;

  private activeTourVariant: ChatTourVariant | null = null;

  constructor() {
    super({
      lastChatPath: null,
      autoSelectPath: null,
      checking: true,
      showAnnouncement: false,
      conversationHistoryId: undefined,
      hasResolvedSession: false,
      shouldOpenDrawer: false
    });
  }

  loggingEnabled = false;
  private log = (message: string, data?: Record<string, unknown>): void => {
    if (this.loggingEnabled) {
      console.info(`[LeapChat] ${message}`, data ?? "");
    }
  };

  setConversationHistoryId = (id: string | undefined): void => {
    if (id !== this.state.conversationHistoryId) {
      this.log("setConversationHistoryId", {
        previous: this.state.conversationHistoryId,
        next: id
      });
      this.patch({ conversationHistoryId: id });
    }
  };

  initTourGuide = (): (() => void) => {
    const appView = Blac.getBloc(AppViewCubit);
    appView.setAllowIonContentScrollY(false);

    const tourGuide = Blac.getBloc(TourGuideCubit);
    tourGuide.setStorage({
      getItem: (key) => StorageController.getItem(key),
      setItem: (key, value) => StorageController.setItem(key, value)
    });

    return () => {
      appView.setAllowIonContentScrollY(true);
    };
  };

  startTourFromSource = (
    variant: ChatTourVariant,
    navigate: (path: string) => void
  ): void => {
    const { conversationHistoryId } = this.state;
    if (!conversationHistoryId) {
      reportErrorSentry(
        new Error("Cannot start chat tour without conversation history ID")
      );
      return;
    }

    const appView = Blac.getBloc(AppViewCubit);
    appView.setAllowIonContentScrollY(false);

    const tourGuide = Blac.getBloc(TourGuideCubit);
    tourGuide.registerTour(
      createChatTour({
        variant,
        navigate,
        conversationHistoryId
      })
    );
    void tourGuide.startTour(CHAT_TOUR_ID, true);

    this.activeTourVariant = variant;
    tracker.track(TrackEvent.SophieChatTour, {
      type: TrackType.start,
      data: { variant }
    });

    const unsubscribe = tourGuide._observer.subscribe({
      id: "sophie-tour-end",
      fn: (newState: { activeTourId: string | null }) => {
        if (newState.activeTourId !== null) {
          return;
        }

        const seen = StorageController.getItem(
          `tourGuide.seen.${CHAT_TOUR_ID}`
        );
        const type = seen === "true" ? TrackType.complete : TrackType.cancel;
        tracker.track(TrackEvent.SophieChatTour, {
          type,
          data: { variant: this.activeTourVariant ?? undefined }
        });

        this.activeTourVariant = null;
        unsubscribe();
      }
    });
  };

  trackPath = (pathname: string): void => {
    if (pathname.startsWith(APP_CHAT_PATH + "/")) {
      this.patch({ lastChatPath: pathname });
    }
  };

  selectAutoRedirectConversation = (
    conversations: {
      id: string;
      createdAt: string;
      unreadMessageCount: number;
    }[]
  ): void => {
    if (conversations.length !== 2) {
      return;
    }
    const conversationsSorted = conversations.toSorted(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    const conversationWithUnread = conversationsSorted.find(
      (conv) => conv.unreadMessageCount > 0
    );
    if (conversationWithUnread) {
      this.patch({
        autoSelectPath: `${chatConversationPath(conversationWithUnread.id)}?showInfoCard`
      });
      tracker.track(TrackEvent.SophieMigrationRedirect, {
        type: TrackType.present,
        data: { conversationId: conversationWithUnread.id }
      });
    }
  };

  markConversationOnboarded = (): void => {
    const { conversationHistoryId } = this.state;
    this.log("markConversationOnboarded called", { conversationHistoryId });

    if (!conversationHistoryId) {
      this.log("markConversationOnboarded skipped: no conversationHistoryId");
      return;
    }

    this.log("markConversationOnboarded clearing preference");

    void Blac.getBloc(UserPreferencesCubit).updateUserPreferences({
      [UserPreferenceKeys.previousMessagesConversationId]: null
    });
  };

  resolveIndexRedirect = (): void => {
    if (this.state.hasResolvedSession) {
      return;
    }

    const { conversationHistoryId } = this.state;
    this.log("resolveIndexRedirect called", { conversationHistoryId });

    void ConversationService.getInstance()
      .listConversations()
      .then((conversations) => {
        this.log("resolveIndexRedirect conversations loaded", {
          count: conversations.length,
          ids: conversations.map((c) => c.id)
        });

        const hasPreviouslyOpened =
          this.state.lastChatPath !== null &&
          this.state.lastChatPath !== APP_CHAT_PATH;

        if (conversationHistoryId) {
          this.checkSophieAnnouncement(conversations, conversationHistoryId);
          if (!hasPreviouslyOpened) {
            this.selectAutoRedirectConversation(conversations);
          }
        }

        if (!this.state.autoSelectPath && !hasPreviouslyOpened) {
          this.resolveAutoOpenRedirect(conversations);
        }
      })
      .catch(() => {
        // fall through to default behavior
      })
      .finally(() => {
        this.patch({ checking: false, hasResolvedSession: true });
      });
  };

  private resolveAutoOpenRedirect = (
    conversations: ConversationResponse[]
  ): void => {
    const newestBy = <T extends { updatedAt: string }>(list: T[]): T =>
      list.toSorted(
        (a, b) => Date.parse(b.updatedAt) - Date.parse(a.updatedAt)
      )[0];

    const unread = conversations.filter((c) => c.unreadMessageCount > 0);
    if (unread.length > 0) {
      this.patch({ autoSelectPath: chatConversationPath(newestBy(unread).id) });
      return;
    }

    const escalated = conversations.filter(
      (c) =>
        c.status === ConversationResponse.status.ACTIVE &&
        ConversationService.isEscalated(c)
    );
    if (escalated.length > 0) {
      this.patch({
        autoSelectPath: chatConversationPath(newestBy(escalated).id)
      });
    }
  };

  clearShouldOpenDrawer = (): void => {
    this.patch({ shouldOpenDrawer: false });
  };

  private checkSophieAnnouncement = (
    conversations: { id: string }[],
    previousConversationId: string
  ): void => {
    const hasOnlyPreviousConversation =
      conversations.length === 1 &&
      conversations[0].id === previousConversationId;

    this.log("checkSophieAnnouncement", {
      conversationCount: conversations.length,
      previousConversationId,
      hasOnlyPreviousConversation
    });

    if (!hasOnlyPreviousConversation) {
      return;
    }

    this.patch({ showAnnouncement: true });
  };

  showSophieAnnouncement = (
    onConfirm: () => void,
    onCancel: () => void,
    force = false
  ): void => {
    if (!this.state.showAnnouncement && !force) {
      return;
    }

    this.patch({ showAnnouncement: false });

    const popup = Blac.getBloc(AnnouncementPopupCubit);
    popup.show({
      title: translate("announcementPopup.sophie.title"),
      body: translate("announcementPopup.sophie.body"),
      confirmText: translate("announcementPopup.sophie.confirmText"),
      cancelText: translate("announcementPopup.sophie.cancelText"),
      image: hiImSophieImage,
      onConfirm,
      onCancel
    });
  };
}
